import React, { useState, useEffect, useCallback } from 'react';
import { profileAPI } from '../services/api';
import './Profile.css';

const Profile = ({ user }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [firebaseFile, setFirebaseFile] = useState(null);
  const [firebaseFileName, setFirebaseFileName] = useState('');

  const fetchProfile = useCallback(async () => {
    try {
      setLoading(true);
      const response = await profileAPI.getProfile(user.email);
      setProfile(response.data.data);
      setMessage({ type: '', text: '' });
    } catch (err) {
      console.error('Failed to fetch profile:', err);
      setMessage({
        type: 'error',
        text: err.response?.data?.error || 'Failed to load profile'
      });
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user && user.email) {
      fetchProfile();
    }
  }, [user, fetchProfile]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type === 'application/json' || file.name.endsWith('.json')) {
        setFirebaseFile(file);
        setFirebaseFileName(file.name);
        setMessage({ type: '', text: '' });
      } else {
        setMessage({ type: 'error', text: 'Please select a JSON file' });
        setFirebaseFile(null);
        setFirebaseFileName('');
      }
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!firebaseFile) {
      setMessage({ type: 'error', text: 'Please select a Firebase JSON file' });
      return;
    }

    if (!user || !user.email) {
      setMessage({ type: 'error', text: 'User email is required' });
      return;
    }

    setUploading(true);
    setMessage({ type: '', text: '' });

    try {
      const formData = new FormData();
      formData.append('firebaseFile', firebaseFile);
      formData.append('email', user.email);

      const response = await profileAPI.uploadFirebase(formData);
      
      setMessage({
        type: 'success',
        text: response.data.message || 'Firebase configuration uploaded successfully!'
      });
      
      // Refresh profile
      await fetchProfile();
      
      // Reset file input
      setFirebaseFile(null);
      setFirebaseFileName('');
      e.target.reset();
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.error || 'Failed to upload Firebase configuration'
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteFirebase = async () => {
    if (!window.confirm('Are you sure you want to delete your Firebase configuration? This will disable push notifications for your account.')) {
      return;
    }

    try {
      await profileAPI.deleteFirebaseConfig(user.email);
      setMessage({
        type: 'success',
        text: 'Firebase configuration deleted successfully'
      });
      await fetchProfile();
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.error || 'Failed to delete Firebase configuration'
      });
    }
  };

  if (loading) {
    return <div className="loading">Loading profile...</div>;
  }

  return (
    <div className="profile">
      <h1 className="page-title">User Profile</h1>

      <div className="card">
        {message.text && (
          <div className={`alert alert-${message.type === 'success' ? 'success' : 'error'}`}>
            {message.text}
          </div>
        )}

        {/* User Information */}
        <div className="profile-section">
          <h2 className="section-title">User Information</h2>
          <div className="profile-info">
            <div className="info-row">
              <span className="info-label">Email:</span>
              <span className="info-value">{user?.email || 'N/A'}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Username:</span>
              <span className="info-value">{user?.username || user?.name || 'N/A'}</span>
            </div>
          </div>
        </div>

        {/* Firebase Configuration */}
        <div className="profile-section">
          <h2 className="section-title">Firebase Configuration</h2>
          <p className="section-description">
            Upload your Firebase service account JSON file to enable push notifications for your account.
            This allows you to send push notifications using your own Firebase project.
          </p>

          {profile?.hasFirebaseConfig ? (
            <div className="firebase-status">
              <div className="status-badge status-success">
                ✓ Firebase Configured
              </div>
              <div className="firebase-details">
                <div className="info-row">
                  <span className="info-label">Project ID:</span>
                  <span className="info-value">{profile.firebaseProjectId}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Client Email:</span>
                  <span className="info-value">{profile.firebaseClientEmail}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Last Updated:</span>
                  <span className="info-value">
                    {profile.updatedAt ? new Date(profile.updatedAt).toLocaleString() : 'N/A'}
                  </span>
                </div>
              </div>
              <button
                className="btn btn-secondary"
                onClick={handleDeleteFirebase}
                style={{ marginTop: '1rem' }}
              >
                Delete Firebase Configuration
              </button>
            </div>
          ) : (
            <div className="firebase-status">
              <div className="status-badge status-warning">
                ⚠ Firebase Not Configured
              </div>
              <p style={{ color: '#666', marginTop: '0.5rem' }}>
                Upload a Firebase service account JSON file to enable push notifications.
              </p>
            </div>
          )}

          <form onSubmit={handleUpload} style={{ marginTop: '1.5rem' }}>
            <div className="form-group">
              <label className="form-label">Upload Firebase Service Account JSON</label>
              <input
                type="file"
                accept=".json,application/json"
                onChange={handleFileChange}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  marginBottom: '0.5rem'
                }}
              />
              {firebaseFileName && (
                <small style={{ color: '#666', display: 'block', marginTop: '0.5rem' }}>
                  Selected: {firebaseFileName}
                </small>
              )}
              <small style={{ color: '#666', display: 'block', marginTop: '0.5rem' }}>
                Download your service account key from Firebase Console → Project Settings → Service Accounts
              </small>
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!firebaseFile || uploading}
            >
              {uploading ? 'Uploading...' : 'Upload Firebase Configuration'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Profile;

